import React, { useState, useEffect, useContext } from 'react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';
import TaskList from './TaskList';
import TaskCalendar from './TaskCalendar';
import TaskForm from './TaskForm';
import { PlusIcon, CalendarIcon, ListBulletIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../../contexts/AuthContext';

const TaskManager = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('list'); // 'list' or 'calendar'
  const [showAddTaskForm, setShowAddTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [viewingTask, setViewingTask] = useState(null);
  const { currentUser } = useContext(AuthContext);
  
  // Filters
  const [filterData, setFilterData] = useState({
    user_id: 'all',
    department_id: 'all',
    group_id: 'all',
    type_id: 'all',
    is_completed: 'false',
    search: ''
  });
  
  // Reference data
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [groups, setGroups] = useState([]);
  const [types, setTypes] = useState([]);
  
  // Set default user filter to current user
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  useEffect(() => {
    // Only set default user on initial load, not when user actively selects "all"
    if (currentUser?.id && filterData.user_id === 'all' && !initialLoadComplete) {
      setFilterData(prev => ({
        ...prev,
        user_id: currentUser.id.toString()
      }));
      setInitialLoadComplete(true);
    }
  }, [currentUser, filterData.user_id, initialLoadComplete]);
  
  // Fetch tasks based on filters
  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query params
      const params = {};
      
      if (filterData.user_id === 'unassigned') {
        params.unassigned = true;
      } else if (filterData.user_id !== 'all') {
        params.assigned_user_id = filterData.user_id;
      }
      
      if (filterData.department_id !== 'all') params.department_id = filterData.department_id;
      if (filterData.group_id !== 'all') params.group_id = filterData.group_id;
      if (filterData.type_id !== 'all') params.type_id = filterData.type_id;
      if (filterData.is_completed !== 'all') params.is_completed = filterData.is_completed;
      if (filterData.search) params.search = filterData.search;
      
      const response = await api.get('/tasks', { params });
      
      if (response.data && response.data.tasks) {
        setTasks(response.data.tasks);
      } else {
        setTasks([]);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks. Please try again later.');
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch reference data
  const fetchReferenceData = async () => {
    try {
      // Fetch users
      const usersResponse = await api.get('/auth/users');
      if (usersResponse.data && usersResponse.data.users) {
        setUsers(usersResponse.data.users);
      }
      
      // Fetch departments
      const departmentsResponse = await api.get('/departments');
      if (departmentsResponse.data && departmentsResponse.data.departments) {
        setDepartments(departmentsResponse.data.departments);
      }
      
      // Fetch groups
      const groupsResponse = await api.get('/groups');
      if (groupsResponse.data && groupsResponse.data.groups) {
        setGroups(groupsResponse.data.groups);
      }
      
      // Fetch types
      const typesResponse = await api.get('/types');
      if (typesResponse.data && typesResponse.data.types) {
        setTypes(typesResponse.data.types);
      }
    } catch (err) {
      console.error('Error fetching reference data:', err);
      toast.error('Failed to load some reference data');
    }
  };
  
  // Load data on component mount
  useEffect(() => {
    const initialLoad = async () => {
      await fetchReferenceData();
      
      // Set the current user as the default filter if available
      if (currentUser?.id && filterData.user_id === 'all' && !initialLoadComplete) {
        setFilterData(prev => ({
          ...prev,
          user_id: currentUser.id.toString()
        }));
        setInitialLoadComplete(true);
        // fetchTasks() will be called by the useEffect that watches filterData
      } else {
        // If no current user or we're not setting the filter, fetch tasks normally
        fetchTasks();
      }
    };
    
    initialLoad();
  }, []);
  
  // Reload tasks when filters change
  useEffect(() => {
    fetchTasks();
  }, [filterData]);
  
  // Handle filter changes
  const handleFilterChange = (name, value) => {
    // If the user is explicitly changing the user_id filter,
    // mark the initial load as complete to prevent auto-reset
    if (name === 'user_id') {
      setInitialLoadComplete(true);
    }
    
    setFilterData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle task creation
  const handleCreateTask = async (taskData) => {
    try {
      const response = await api.post('/tasks', taskData);
      
      if (response.data && response.data.task) {
        toast.success(response.data.message || 'Task created successfully');
        setShowAddTaskForm(false);
        fetchTasks(); // Reload tasks
      }
    } catch (err) {
      console.error('Error creating task:', err);
      toast.error(err.response?.data?.error || 'Failed to create task');
    }
  };
  
  // Handle task update
  const handleUpdateTask = async (updatedTask) => {
    try {
      setLoading(true);
      
      // Update the task through API
      const response = await api.put(`/tasks/${updatedTask.id}`, updatedTask);
      
      if (response.data && response.data.task) {
        toast.success(response.data.message || 'Task updated successfully');
        
        // Reset states after successful update
        setViewingTask(null);
        setEditingTask(null);
        setShowAddTaskForm(false);
        
        // Refresh tasks
        fetchTasks();
      }
      
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error('Error updating task:', err);
      toast.error(err.response?.data?.error || 'Failed to update task');
    }
  };
  
  // Handle task deletion
  const handleDeleteTask = async (taskId, deleteSeries = false, deleteFuture = false) => {
    try {
      const response = await api.delete(`/tasks/${taskId}`, {
        params: { 
          delete_series: deleteSeries,
          delete_future: deleteFuture
        }
      });
      
      toast.success(response.data?.message || 'Task deleted successfully');
      fetchTasks(); // Reload tasks
    } catch (err) {
      console.error('Error deleting task:', err);
      toast.error(err.response?.data?.error || 'Failed to delete task');
    }
  };
  
  // Handle marking task as complete/incomplete
  const handleToggleComplete = async (taskId, isCompleted) => {
    try {
      const response = await api.put(`/tasks/${taskId}/complete`, {
        is_completed: isCompleted
      });
      
      if (response.data && response.data.task) {
        toast.success(response.data.message || `Task marked as ${isCompleted ? 'completed' : 'incomplete'}`);
        fetchTasks(); // Reload tasks
      }
    } catch (err) {
      console.error('Error updating task completion status:', err);
      toast.error(err.response?.data?.error || 'Failed to update task status');
    }
  };
  
  // Handle task edit button click
  const handleEditClick = (task) => {
    setEditingTask(task);
    setViewingTask(null);
    setShowAddTaskForm(true);
  };
  
  // Handle task view button click
  const handleViewClick = (task) => {
    setViewingTask(task);
    setEditingTask(null);
    setShowAddTaskForm(true);
  };
  
  // Handle switch from view to edit mode
  const handleSwitchToEdit = (task) => {
    console.log("Switching to edit mode with task:", task);
    setViewingTask(null);
    setEditingTask(task);
    // No need to close and reopen the form, just transition states
  };
  
  return (
    <div className="px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '20px' }}>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-white">Tasks</h1>
          <p className="mt-2 text-sm text-gray-300">
            Manage your personal and team tasks
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none flex items-center space-x-3">
          <button
            type="button"
            onClick={() => setView(view === 'list' ? 'calendar' : 'list')}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
          >
            {view === 'list' ? (
              <>
                <CalendarIcon className="h-4 w-4 mr-2" />
                Calendar View
              </>
            ) : (
              <>
                <ListBulletIcon className="h-4 w-4 mr-2" />
                List View
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingTask(null);
              setShowAddTaskForm(true);
            }}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Task
          </button>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="mt-6 bg-gray-800 p-4 rounded-lg border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* User Filter */}
          <div>
            <label htmlFor="user-filter" className="block text-sm font-medium text-gray-300 mb-1">
              User
            </label>
            <select
              id="user-filter"
              value={filterData.user_id}
              onChange={(e) => handleFilterChange('user_id', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="all">All Users</option>
              <option value="unassigned">Unassigned</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Department Filter */}
          <div>
            <label htmlFor="department-filter" className="block text-sm font-medium text-gray-300 mb-1">
              Department
            </label>
            <select
              id="department-filter"
              value={filterData.department_id}
              onChange={(e) => handleFilterChange('department_id', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="all">All Departments</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Group Filter */}
          <div>
            <label htmlFor="group-filter" className="block text-sm font-medium text-gray-300 mb-1">
              Group
            </label>
            <select
              id="group-filter"
              value={filterData.group_id}
              onChange={(e) => handleFilterChange('group_id', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="all">All Groups</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Type Filter */}
          <div>
            <label htmlFor="type-filter" className="block text-sm font-medium text-gray-300 mb-1">
              Type
            </label>
            <select
              id="type-filter"
              value={filterData.type_id}
              onChange={(e) => handleFilterChange('type_id', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="all">All Types</option>
              {types.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Second row of filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {/* Completion Status Filter */}
          <div>
            <label htmlFor="status-filter" className="block text-sm font-medium text-gray-300 mb-1">
              Status
            </label>
            <select
              id="status-filter"
              value={filterData.is_completed}
              onChange={(e) => handleFilterChange('is_completed', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="false">Active Tasks</option>
              <option value="true">Completed Tasks</option>
              <option value="all">All Tasks</option>
            </select>
          </div>
          
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-300 mb-1">
              Search
            </label>
            <input
              type="text"
              id="search"
              value={filterData.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Search tasks..."
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        </div>
      </div>
      
      {/* Task Content */}
      <div className="mt-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-900/50 p-4 rounded-md border border-red-700">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm font-medium text-red-300">{error}</p>
                <button
                  onClick={fetchTasks}
                  className="mt-2 text-red-400 hover:text-red-300 text-sm underline"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        ) : view === 'list' ? (
          <TaskList
            tasks={tasks}
            onDelete={handleDeleteTask}
            onEdit={handleEditClick}
            onView={handleViewClick}
            onToggleComplete={handleToggleComplete}
            users={users}
            departments={departments}
            groups={groups}
            types={types}
          />
        ) : (
          <TaskCalendar
            tasks={tasks}
            onDelete={handleDeleteTask}
            onEdit={handleEditClick}
            onView={handleViewClick}
            onToggleComplete={handleToggleComplete}
            users={users}
          />
        )}
      </div>
      
      {/* Task Form Modal */}
      {showAddTaskForm && (
        <TaskForm
          isOpen={showAddTaskForm}
          onClose={() => {
            setShowAddTaskForm(false);
            setEditingTask(null);
            setViewingTask(null);
          }}
          onSubmit={
            editingTask ? 
              (data) => handleUpdateTask({...data, id: editingTask.id}) :
              viewingTask ? () => {} : handleCreateTask
          }
          onSwitchToEdit={viewingTask ? handleSwitchToEdit : undefined}
          task={editingTask || viewingTask}
          users={users}
          departments={departments}
          groups={groups}
          types={types}
          viewOnly={!!viewingTask}
        />
      )}
    </div>
  );
};

export default TaskManager; 